import React from 'react';
import { Briefcase, Calendar, MapPin } from 'lucide-react';
import { experience } from '../mock';
import { useTheme } from '../context/ThemeContext';

const Experience = () => {
  const { theme } = useTheme();

  return (
    <section id="experience" className={`py-12 sm:py-20 px-4 sm:px-6 lg:px-8 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-[#1B3139] to-[#0f1f26]' 
        : 'bg-gradient-to-br from-white to-gray-100'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center space-x-2 mb-4">
            <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF3621]" />
            <span 
              className="text-[#FF3621] font-semibold text-xs sm:text-sm uppercase tracking-wider"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Career Journey
            </span>
          </div>
          <h2 
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Professional Experience
          </h2>
          <p 
            className={`text-base sm:text-lg max-w-2xl mx-auto px-4 ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-600'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            13+ years of architecting data and AI solutions for enterprise clients worldwide
          </p>
        </div>

        <div className="relative">
          {/* Timeline Line - Desktop only */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-[#FF3621] via-blue-500 to-[#FF3621]"></div>

          {/* Mobile Timeline Line */}
          <div className="md:hidden absolute left-4 top-0 h-full w-0.5 bg-gradient-to-b from-[#FF3621] via-blue-500 to-[#FF3621]"></div>

          {/* Experience Items */}
          <div className="space-y-6 sm:space-y-12">
            {experience.map((exp, index) => (
              <div
                key={exp.id}
                className={`relative flex items-start md:items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline Dot - Desktop */}
                <div className={`hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-[#FF3621] rounded-full border-4 z-10 ${
                  theme === 'dark' ? 'border-[#1B3139]' : 'border-white'
                }`}></div>

                {/* Timeline Dot - Mobile */}
                <div className={`md:hidden absolute left-4 transform -translate-x-1/2 w-3 h-3 bg-[#FF3621] rounded-full border-2 z-10 mt-6 ${
                  theme === 'dark' ? 'border-[#1B3139]' : 'border-white'
                }`}></div>

                {/* Content Card */}
                <div className={`w-full pl-8 md:pl-0 md:w-[calc(50%-2rem)] ${
                  index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'
                }`}>
                  <div className={`border rounded-lg p-4 sm:p-6 transition-all duration-300 shadow-xl hover:shadow-2xl group ${
                    theme === 'dark' 
                      ? 'bg-[#1B3139] border-white/10 hover:border-[#FF3621]/50' 
                      : 'bg-white border-gray-200 hover:border-[#FF3621]/50'
                  }`}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-4 gap-2">
                      <div className="flex items-center space-x-2 text-[#FF3621]">
                        <Calendar className="h-4 w-4" />
                        <span 
                          className="text-xs sm:text-sm font-semibold"
                          style={{ fontFamily: 'DM Mono, monospace' }}
                        >
                          {exp.period}
                        </span>
                      </div>
                      <div className={`flex items-center space-x-2 ${
                        theme === 'dark' ? 'text-white/60' : 'text-gray-500'
                      }`}>
                        <MapPin className="h-4 w-4" />
                        <span 
                          className="text-xs sm:text-sm"
                          style={{ fontFamily: 'DM Sans, sans-serif' }}
                        >
                          {exp.location}
                        </span>
                      </div>
                    </div>

                    <h3 
                      className={`text-lg sm:text-xl font-bold mb-2 group-hover:text-[#FF3621] transition-colors ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {exp.title}
                    </h3>

                    <p 
                      className={`text-base sm:text-lg mb-3 sm:mb-4 font-semibold ${
                        theme === 'dark' ? 'text-white/80' : 'text-gray-700'
                      }`}
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {exp.company}
                    </p>

                    <p 
                      className={`text-sm sm:text-base mb-3 sm:mb-4 leading-relaxed ${
                        theme === 'dark' ? 'text-white/60' : 'text-gray-600'
                      }`}
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {exp.description}
                    </p>

                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {exp.technologies.map((tech, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded text-xs font-medium"
                          style={{ fontFamily: 'DM Mono, monospace' }}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
